import $ from 'jquery';
import _ from 'lodash';
import Helpers from '../utilities/helpers';
import DATA_CONFIG from '../config';

const CONFIG = {
    ITEM: '.js-dropSearch-item',
    TOGGLE: '.js-dropSearch-toggle',
    CLOSE: '.js-dropSearch-close',
    SEARCH_BOX: '.js-dropSearch-input',
    COUNT: '.js-dropSearch-count',
    LIST_CONTAINER: '.js-dropSearch-list',
    TRANSITION_LENGTH: 250,
    NO_RESULTS: {
        centerName: 'No centers found in search',
        centerCode: ''
    }
};

/**
 * @fileOverview View handles updating the visual elements on the page
 */
export default class DropSearchView {
    /**
     * Construct the view and run initialization
     *
     * @param element
     */
    constructor(element) {
        /**
         * Check to see if elements are passed in as jQuery objects or strings
         */
        this.$element = (element instanceof $) ? element : $(element);

        /**
         * If element doesn't exist, exit early
         */
        if (!this.$element.length) {
            return;
        }

        /**
         * sets handlebars templates up as variables to be used to build the result list
         */
        this.dropListItemTemplate = window.JST['templates/JST/dropListItem'];

        /**
         * listOfCenters is JSON object with information about each center
         *
         * @type {object}
         */
        this.listOfCenters = null;

        /**
         * Load helpers
         *
         * @type {Helpers}
         */
        this.helpers = new Helpers();

        /**
         * Import JSON data
         */
        this.helpers.fromJSON(DATA_CONFIG.LIST_OF_CENTERS).then (response => {
            this.listOfCenters = response;
            this.init();
        });
    }

    /**
     * Initialize the view
     *
     * @method init
     */
    init() {
        this.isEnabled = false;
        this.isActive = false;

        return this.setupHandlers()
                   .createChildren()
                   .layout()
                   .enable();
    }

    /**
     * Binds the scope of handler functions
     * Should be run once on view initialization
     *
     * @method setupHandlers
     * @returns {DropSearchView}
     */
    setupHandlers() {
        this.handleItemClick = this.onItemClick.bind(this);
        this.handleToggleClick = this.onToggleClick.bind(this);
        this.handleCloseClick = this.onCloseClick.bind(this);
        this.handleAnyClick = this.onAnyClick.bind(this);
        this.handleKeydown = this.onKeydown.bind(this);
        this.handleSearchKeydown = this.onSearchKeydown.bind(this);

        return this;
    }

    /**
     * Create any child objects and references to DOM elements
     * Should only be run once on init of the view
     *
     * @method createChildren
     * @returns {DropSearchView}
     */
    createChildren() {
        this.$toggle = $(CONFIG.TOGGLE);
        this.$close = this.$element.find(CONFIG.CLOSE);
        this.$searchBox = this.$element.find(CONFIG.SEARCH_BOX);
        this.$listContainer = this.$element.find(CONFIG.LIST_CONTAINER);
        this.$count = this.$element.find(CONFIG.COUNT);

        return this;
    }

    /**
     * Update the DOM to reflect the current state of the model
     *
     * @method layout
     * @returns {DropSearchView}
     */
    layout() {
        this.populateList(this.listOfCenters);

        return this;
    }

    /**
     * Disable, update the DOM, and then re-enable to create handlers for new DOM
     *
     * @method render
     */
    render() {
        this.disable();
        this.layout();
        this.enable();
    }

    /**
     * Enables the view
     * Performs binding to handlers
     * Exits early if already enabled
     *
     * @method enable
     * @returns {DropSearchView}
     */
    enable() {
        if (this.isEnabled) {
            return this;
        }

        this.isEnabled = true;

        for (let i = 0; i < this.$item.length; i++) {
            this.$item[i].addEventListener('click', this.handleItemClick);
        }

        for (let i = 0; i < this.$toggle.length; i++) {
            this.$toggle[i].addEventListener('click', this.handleToggleClick);
        }

        for (let i = 0; i < this.$close.length; i++) {
            this.$close[i].addEventListener('click', this.handleCloseClick);
        }

        for (let i = 0; i < this.$searchBox.length; i++) {
            this.$searchBox[i].addEventListener('input', this.handleSearchKeydown);
        }

        $('html').on('click', this.handleAnyClick);
        $('html').on('touchend', this.handleAnyClick);

        document.addEventListener('keydown', this.handleKeydown);

        return this;
    }

    /**
     * Disables the view
     * Tears down any event binding to handlers
     * Exits early if it is already disabled
     *
     * @method disable
     * @returns {DropSearchView}
     */
    disable() {
        if (!this.isEnabled) {
            return this;
        }

        for (let i = 0; i < this.$item.length; i++) {
            this.$item[i].removeEventListener('click', this.handleItemClick);
        }

        for (let i = 0; i < this.$toggle.length; i++) {
            this.$toggle[i].removeEventListener('click', this.handleToggleClick);
        }

        for (let i = 0; i < this.$close.length; i++) {
            this.$close[i].removeEventListener('click', this.handleCloseClick);
        }

        for (let i = 0; i < this.$searchBox.length; i++) {
            this.$searchBox[i].removeEventListener('input', this.handleSearchKeydown);
        }

        document.removeEventListener('keydown', this.handleKeydown);

        this.isEnabled = false;

        return this;
    }

    /**
     * Destroys the view
     * Tears down any events, handlers, elements
     * Should be called when the object should be left unused
     *
     * @method destroy
     * @returns {DropSearchView}
     */
    destroy() {
        this.disable();

        return this;
    }

    /**
     * Return a list of items filtered by search input
     *
     * @param objects
     * @returns {DropSearchView}
     */
    populateList(objects) {
        const searchedObjects = _.filter(objects, (obj) => {
            return (obj.centerName.toUpperCase().indexOf(this.$searchBox.val().toUpperCase()) > -1 ||
                    obj.centerCode.toUpperCase().indexOf(this.$searchBox.val().toUpperCase()) > -1);
        });
        this.generateListMarkup(searchedObjects);
        this.updateResultCount(searchedObjects.length);

        return this;
    }

    /**
     * Generate markup for list of centers based on search results
     *
     * @param objects
     * @returns {DropSearchView}
     */
    generateListMarkup(objects) {
        this.$listContainer.empty();
        let listLayout = '';

        if (objects.length > 0) {
            _.forEach(objects, (value) => {
                listLayout += this.dropListItemTemplate(value);
            });
        } else {
            listLayout += this.dropListItemTemplate(CONFIG.NO_RESULTS);
        }

        this.$listContainer.append(listLayout);

        this.$item = this.$element.find(CONFIG.ITEM);

        return this;
    }

    /**
     * Updates count of listed results
     *
     * @param count
     */
    updateResultCount(count) {
        this.$count.empty().append(`${count} Results`);
    }

    /**
     * When key is inputted into search box, update the DOM
     *
     * @param event
     * @returns {DropSearchView}
     */
    onSearchKeydown(event) {
        this.render();

        return this;
    }

    /**
     * Handles item click event
     *
     * @param event
     * @returns {DropSearchView}
     */
    onItemClick(event) {
        const $target = $(event.target).closest(CONFIG.ITEM);

        if (this.isActive) {
            event.preventDefault();
            var centerId = $target.data('centerId');
            $.post(DATA_CONFIG.CENTER_SELECT_PATH, { centerId: centerId})
            .done(function(data) {
                location.reload();
            });
        }

        return this;
    }

    /**
     * Handles toggle click event
     *
     * @param event
     * @returns {DropSearchView}
     */
    onToggleClick(event) {
        if (this.isActive) {
            this.close(this.$element);
        } else {
            this.open(this.$element);
        }

        return this;
    }

    /**
     * Handles close click event
     *
     * @param event
     * @returns {DropSearchView}
     */
    onCloseClick(event) {
        if (this.isActive) {
            this.close(this.$element);
        }

        return this;
    }

    /**
     * Watch for page clicks and close the dropdown if it is outside the dropdown area
     *
     * @param event
     * @returns {DropSearchView}
     */
    onAnyClick(event) {
        const $target = $(event.target);

        if (this.isActive && $target.closest(this.$element).length === 0 && !$target.hasClass(CONFIG.TOGGLE.substring(1))) {
            this.close(this.$element);
        }

        return this;
    }

    /**
     * Handles keydown event
     * Close dropdown if user hits ESC
     *
     * @param event
     * @returns {DropSearchView}
     */
    onKeydown(event) {
        if ((this.isActive == true) && ((!event.keyCode) || (event.keyCode === 27))) {
            this.close(this.$element);
        }

        return this;
    }

    /**
     * Open the dropdown
     *
     * @param $element
     * @returns {DropSearchView}
     */
    open($element) {
        $element.addClass('open').slideDown(CONFIG.TRANSITION_LENGTH);
        this.$searchBox.focus();
        this.isActive = true;
        this.render();

        return this;
    }

    /**
     * Close the dropdown
     *
     * @param $element
     * @returns {DropSearchView}
     */
    close($element) {
        $element.removeClass('open').slideUp(CONFIG.TRANSITION_LENGTH);
        this.$searchBox.val('');
        this.isActive = false;
        this.render();

        return this;
    }
}
